/**
 * Email Ad Validation Dashboard page
 */
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Box } from '@mui/material';

import Button from '../../components/Button';
import NoData from '../../components/NoData';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import EmailAdValidationFilters from './components/ValidationRunFilters.jsx';
import HistoricalRunsTable from './components/ValidationRunTable.jsx';
import NewRunForm from './components/NewRunForm.jsx';
import ConfirmationDialog from './components/ConfirmationDialog.jsx';
import { emailAdValidationStyles } from './styles';
import useEmailAdValidationData from './hooks/useEmailAdValidationData';

const style = emailAdValidationStyles;

const EmailAdValidation = () => {
  // Use the custom hook for data management
  const {
    activeView,
    historicalRuns,
    totalCount,
    currentPage,
    filters,
    loading,
    error,
    confirmDialog,
    handleFormSubmit,
    handleTableAction,
    handlePageChange,
    handleFiltersChange,
    clearFilters,
    switchToForm,
    switchToTable,
    closeConfirmDialog,
    confirmDialogAction,
  } = useEmailAdValidationData();

  const handleSelectedFilters = (filters) => {
    // Use the new handleFiltersChange to apply all filters at once
    handleFiltersChange(filters);
  };

  const handleClearedFilterState = () => {
    clearFilters();
  };

  const handleCreateNewRun = () => {
    switchToForm();
  };

  const handleCancelNewRun = () => {
    switchToTable();
  };

  const handleSubmitNewRun = async (formData) => {
    try {
      const result = await handleFormSubmit(formData);
      if (result?.success) {
        // Use switchToTable to trigger data refresh after successful submission
        switchToTable();
      }
    } catch (err) {
      // Handle error silently or show user feedback
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        height: '88vh',
        overflow: 'auto',
        padding: { xs: '8px', sm: '16px', md: '24px' },
      }}
    >
      <Grid container spacing={3}>
        <Grid item md={12} lg={12} style={style.validationContainer}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography className={'email-validation-title'} style={style.title} variant={'h5'}>
              {activeView === 'table' ? 'Validation Runs Dashboard' : 'Create New Run'}
              <div>
                {activeView === 'table' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    style={style.button}
                    startIcon={<AddBoxIcon style={style.icon} />}
                    onClick={handleCreateNewRun}
                  >
                    Create New Run
                  </Button>
                )}
              </div>
            </Typography>
          </Grid>

          {activeView === 'table' && (
            <Grid container style={{ marginBottom: 20 }}>
              <Grid
                item
                container
                justifyContent="end"
                style={{ marginBottom: 20 }}
                className="validation-listing"
              >
                <EmailAdValidationFilters
                  handleSelectedFilters={handleSelectedFilters}
                  handleClearedFilterState={handleClearedFilterState}
                  filters={filters}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <div style={{ marginBottom: '60px', overflowX: 'auto', width: '100%' }}>
        {loading && (
          <ShimmerComponent
            updatedStyle={{
              width: '100%',
              height: '100%',
              backgroundSize: '2000px 800px',
              border: '0px',
              margin: 'auto',
              padding: '0 20px',
            }}
          >
            <BouncingLogo height="72vh" text="Loading..." />
          </ShimmerComponent>
        )}

        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <NoData textValue="Error loading data" />
          </Box>
        )}

        {!loading && !error && activeView === 'form' && (
          <NewRunForm onSubmit={handleSubmitNewRun} onCancel={handleCancelNewRun} />
        )}

        {!loading && !error && activeView === 'table' && (
          <HistoricalRunsTable
            historicalRuns={historicalRuns}
            onAction={handleTableAction}
            totalCount={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialogAction}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        variant="delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Container>
  );
};

export default EmailAdValidation;
